<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { memoryApi, type MemoryFileMeta } from '@/lib/memoryApi'
import { ICONS } from '@/lib/icons'

/* One file attached to memory: images show inline (token-fetched blob URL,
   like Matrix media); everything else is a chip that opens/downloads. */
const props = defineProps<{ file: MemoryFileMeta }>()

const url = ref<string | null>(null)
const isImage = computed(() => props.file.mime.startsWith('image/'))

watch(
  () => props.file.id,
  async () => {
    url.value = null
    if (props.file.mime.startsWith('image/')) url.value = await memoryApi.fileObjectUrl(props.file.id)
  },
  { immediate: true },
)

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

async function open() {
  const u = url.value || (await memoryApi.fileObjectUrl(props.file.id))
  if (!u) return
  if (isImage.value || props.file.mime === 'application/pdf') {
    window.open(u, '_blank', 'noopener')
  } else {
    const a = document.createElement('a')
    a.href = u
    a.download = props.file.name
    a.click()
  }
}
</script>

<template>
  <figure v-if="isImage" class="mem__att mem__att--img">
    <img v-if="url" :src="url" :alt="file.name" loading="lazy" draggable="false" @click="open" />
    <span v-else class="mem__att-ph" />
    <figcaption>{{ file.name }} · {{ fmtSize(file.size) }}</figcaption>
  </figure>
  <button v-else class="mem__att mem__att--doc" type="button" :title="`Open ${file.name}`" @click="open">
    <span class="ico" v-html="ICONS.doc" />
    <span class="mem__att-name">{{ file.name }}</span>
    <span class="mem__att-size">{{ fmtSize(file.size) }}</span>
  </button>
</template>
